/**
 * MEVIA - IPS Gauge Component
 * Tacómetro visual para mostrar el Índice de Permanencia Saludable (IPS)
 * Escala de 0-100 con niveles de color: Crítico, Alto Riesgo, Moderado, Saludable
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Activity, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle,
  Info,
  ChevronDown,
  ChevronUp,
  Eye
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

// IPS Level configuration
const IPS_LEVELS = {
  SALUDABLE: { min: 70, max: 100, color: '#22C55E', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  MODERADO: { min: 50, max: 69, color: '#F59E0B', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  ALTO_RIESGO: { min: 30, max: 49, color: '#F97316', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  CRITICO: { min: 0, max: 29, color: '#EF4444', bgColor: 'bg-red-100', textColor: 'text-red-700' }
};

const LEVEL_LABELS = {
  es: {
    SALUDABLE: 'Saludable',
    MODERADO: 'Moderado',
    ALTO_RIESGO: 'Alto Riesgo',
    CRITICO: 'Crítico'
  },
  en: {
    SALUDABLE: 'Healthy',
    MODERADO: 'Moderate',
    ALTO_RIESGO: 'High Risk',
    CRITICO: 'Critical'
  }
};

/**
 * SVG Gauge Component - Tacómetro semicircular
 */
const GaugeSVG = ({ value, size = 200, strokeWidth = 20 }) => {
  const normalizedValue = Math.max(0, Math.min(100, value));
  
  // SVG dimensions
  const center = size / 2;
  const radius = (size - strokeWidth) / 2 - 10;
  
  // Arc calculations (180 degrees = semicircle)
  const startAngle = 180;
  const endAngle = 0;
  const angleRange = 180;
  
  // Value angle
  const valueAngle = startAngle - (normalizedValue / 100) * angleRange;
  
  // Helper to convert degrees to radians
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  
  // Calculate arc path
  const getArcPath = (startDeg, endDeg, r) => {
    const start = {
      x: center + r * Math.cos(toRadians(startDeg)),
      y: center - r * Math.sin(toRadians(startDeg))
    };
    const end = {
      x: center + r * Math.cos(toRadians(endDeg)),
      y: center - r * Math.sin(toRadians(endDeg))
    };
    const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };
  
  // Get color based on value
  const getColor = (val) => {
    if (val >= 70) return IPS_LEVELS.SALUDABLE.color;
    if (val >= 50) return IPS_LEVELS.MODERADO.color;
    if (val >= 30) return IPS_LEVELS.ALTO_RIESGO.color;
    return IPS_LEVELS.CRITICO.color;
  };
  
  // Needle position
  const needleLength = radius - 15;
  const needleAngle = toRadians(valueAngle);
  const needleEnd = {
    x: center + needleLength * Math.cos(needleAngle),
    y: center - needleLength * Math.sin(needleAngle)
  };
  
  return (
    <svg width={size} height={size / 2 + 30} viewBox={`0 0 ${size} ${size / 2 + 30}`}>
      {/* Background track with gradient segments */}
      <defs>
        <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={IPS_LEVELS.CRITICO.color} />
          <stop offset="30%" stopColor={IPS_LEVELS.ALTO_RIESGO.color} />
          <stop offset="50%" stopColor={IPS_LEVELS.MODERADO.color} />
          <stop offset="70%" stopColor={IPS_LEVELS.SALUDABLE.color} />
        </linearGradient>
      </defs>
      
      {/* Background arc (gray) */}
      <path
        d={getArcPath(startAngle, endAngle, radius)}
        fill="none"
        stroke="#E5E7EB"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      
      {/* Colored segments */}
      {/* Critical: 0-30 (180° to 126°) */}
      <path
        d={getArcPath(180, 126, radius)}
        fill="none"
        stroke={IPS_LEVELS.CRITICO.color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        opacity={0.3}
      />
      {/* High Risk: 30-50 (126° to 90°) */}
      <path
        d={getArcPath(126, 90, radius)}
        fill="none"
        stroke={IPS_LEVELS.ALTO_RIESGO.color}
        strokeWidth={strokeWidth}
        opacity={0.3}
      />
      {/* Moderate: 50-70 (90° to 54°) */}
      <path
        d={getArcPath(90, 54, radius)}
        fill="none"
        stroke={IPS_LEVELS.MODERADO.color}
        strokeWidth={strokeWidth}
        opacity={0.3}
      />
      {/* Healthy: 70-100 (54° to 0°) */}
      <path
        d={getArcPath(54, 0, radius)}
        fill="none"
        stroke={IPS_LEVELS.SALUDABLE.color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        opacity={0.3}
      />
      
      {/* Value arc (filled portion) */}
      <path
        d={getArcPath(startAngle, valueAngle, radius)}
        fill="none"
        stroke={getColor(normalizedValue)}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        style={{
          transition: 'stroke-dashoffset 0.5s ease-in-out',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
        }}
      />
      
      {/* Needle */}
      <line
        x1={center}
        y1={center}
        x2={needleEnd.x}
        y2={needleEnd.y}
        stroke="#374151"
        strokeWidth={3}
        strokeLinecap="round"
        style={{ transition: 'all 0.5s ease-in-out' }}
      />
      
      {/* Needle center circle */}
      <circle cx={center} cy={center} r={8} fill="#374151" />
      <circle cx={center} cy={center} r={4} fill="#9CA3AF" />
      
      {/* Value labels */}
      <text x={15} y={size / 2 + 20} fontSize="12" fill="#6B7280" fontWeight="500">0</text>
      <text x={size - 25} y={size / 2 + 20} fontSize="12" fill="#6B7280" fontWeight="500">100</text>
      
      {/* Center value - moved up to avoid overlap with badge */}
      <text 
        x={center} 
        y={center + 25} 
        textAnchor="middle" 
        fontSize="26" 
        fontWeight="bold" 
        fill={getColor(normalizedValue)}
      >
        {normalizedValue.toFixed(1)}
      </text>
    </svg>
  );
};

/**
 * IPS Breakdown Component - Muestra el desglose de los 7 indicadores
 */
const IPSBreakdown = ({ breakdown }) => {
  const { language } = useLanguage();
  const [expanded, setExpanded] = React.useState(false);
  
  if (!breakdown || breakdown.length === 0) return null;
  
  const sortedBreakdown = [...breakdown].sort((a, b) => b.contribution - a.contribution);
  
  return (
    <div className="mt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        data-testid="ips-breakdown-toggle"
      >
        <Info className="h-4 w-4" />
        {language === 'es' ? 'Ver desglose del IPS' : 'View IPS breakdown'}
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      
      {expanded && (
        <div className="mt-3 space-y-2 bg-gray-50 p-3 rounded-lg" data-testid="ips-breakdown-content">
          <p className="text-xs text-gray-500 mb-2">
            {language === 'es' 
              ? 'Contribución de cada indicador al IPS total:'
              : 'Contribution of each indicator to total IPS:'}
          </p>
          {sortedBreakdown.map((item) => (
            <div key={item.indicator} className="flex items-center gap-2">
              <span className="w-20 text-xs font-medium text-gray-600 uppercase">
                {item.indicator}
              </span>
              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${(item.contribution / 25) * 100}%`,
                    backgroundColor: item.contribution >= 10 ? '#22C55E' : item.contribution >= 5 ? '#F59E0B' : '#EF4444'
                  }}
                />
              </div>
              <span className="w-16 text-xs text-right text-gray-600">
                +{item.contribution.toFixed(1)}
              </span>
              <span className="w-8 text-xs text-gray-400">
                ({(item.weight * 100).toFixed(0)}%)
              </span>
            </div>
          ))}
          {sortedBreakdown[0]?.inverted && (
            <p className="text-xs text-gray-400 mt-2 italic">
              {language === 'es' 
                ? '* IRR se invierte: menor riesgo = mayor contribución al IPS'
                : '* IRR is inverted: lower risk = higher IPS contribution'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Main IPS Gauge Component - Redesigned with 2 columns
 * Left: Gauge chart with SCORE MEVIA header
 * Right: Breakdown of indicators
 */
const IPSGauge = ({ 
  ips, 
  ipsLevel, 
  levelColor,
  description,
  recommendedAction,
  breakdown,
  showBreakdown = true,
  size = 'medium',
  showCard = true,
  title,
  subtitle
}) => {
  const { language } = useLanguage();
  
  // Size configurations
  const sizes = {
    small: { gauge: 160, container: 'w-48' },
    medium: { gauge: 180, container: 'w-56' },
    large: { gauge: 220, container: 'w-72' }
  };
  
  const sizeConfig = sizes[size] || sizes.medium;
  
  // Get level configuration
  const levelConfig = IPS_LEVELS[ipsLevel] || IPS_LEVELS.MODERADO;
  const levelLabel = LEVEL_LABELS[language]?.[ipsLevel] || ipsLevel;
  
  // Determine trend icon
  const TrendIcon = ips >= 70 ? CheckCircle : ips >= 50 ? TrendingUp : ips >= 30 ? AlertTriangle : TrendingDown;
  
  const content = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="ips-gauge">
      {/* Left Column: Gauge */}
      <div className="flex flex-col items-center text-center">
        {/* SCORE MEVIA Header */}
        <p className="text-base font-bold text-gray-700 uppercase tracking-wide mb-0">
          SCORE MEVIA
        </p>
        <p className="text-sm text-gray-500 mb-3">
          {language === 'es' ? 'Permanencia Saludable' : 'Healthy Retention'}
        </p>
        
        {/* Gauge */}
        <div className={`${sizeConfig.container} mx-auto`}>
          <GaugeSVG value={ips || 0} size={sizeConfig.gauge} />
        </div>
        
        {/* Level Badge - more margin top to avoid overlap */}
        <Badge 
          className={`mt-4 ${levelConfig.bgColor} ${levelConfig.textColor} border-0 text-sm font-semibold px-3 py-1`}
          data-testid="ips-level-badge"
        >
          <TrendIcon className="h-4 w-4 mr-1" />
          {levelLabel}
        </Badge>
        
        {/* Description */}
        {description && (
          <p className="mt-4 text-sm text-gray-600 text-center max-w-xs mx-auto">
            {description}
          </p>
        )}
        
        {/* Recommended Action */}
        {recommendedAction && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg w-full max-w-xs mx-auto text-left">
            <p className="text-xs text-gray-500 font-semibold mb-1">
              {language === 'es' ? 'Acción recomendada:' : 'Recommended action:'}
            </p>
            <p className="text-sm text-gray-700">{recommendedAction}</p>
          </div>
        )}
      </div>
      
      {/* Right Column: Breakdown */}
      {showBreakdown && breakdown && breakdown.length > 0 && (
        <div className="flex flex-col justify-center">
          <p className="text-sm font-semibold text-gray-700 mb-3">
            {language === 'es' ? 'Contribución por indicador:' : 'Contribution by indicator:'}
          </p>
          <div className="space-y-2 bg-gray-50 p-4 rounded-lg">
            {[...breakdown].sort((a, b) => b.contribution - a.contribution).map((item) => (
              <div key={item.indicator} className="flex items-center gap-3">
                <span className="w-12 text-sm font-bold text-gray-600 uppercase">
                  {item.indicator}
                </span>
                <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.min((item.contribution / 25) * 100, 100)}%`,
                      backgroundColor: item.contribution >= 10 ? '#22C55E' : item.contribution >= 5 ? '#F59E0B' : '#EF4444'
                    }}
                  />
                </div>
                <span className="w-20 text-sm text-right text-gray-700 font-medium">
                  +{item.contribution.toFixed(1)} <span className="text-gray-400">({(item.weight * 100).toFixed(0)}%)</span>
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2 italic">
            {language === 'es' 
              ? '* IRR se invierte: menor riesgo = mayor contribución al IPS'
              : '* IRR is inverted: lower risk = higher IPS contribution'}
          </p>
        </div>
      )}
    </div>
  );
  
  if (!showCard) {
    return content;
  }
  
  return (
    <Card className="border-l-4" style={{ borderLeftColor: levelConfig.color }} data-testid="ips-gauge-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center text-lg">
          <Activity className="h-5 w-5 mr-2" style={{ color: levelConfig.color }} />
          {title || (language === 'es' ? 'Indicadores de Recursos Humanos' : 'HR Indicators')}
        </CardTitle>
        {subtitle && (
          <CardDescription>{subtitle}</CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
};

/**
 * Large Donut Chart SVG Component for IPS Distribution
 * Diseño mejorado con dona más gruesa y texto centrado legible
 */
const LargeDonutChart = ({ distribution, size = 180 }) => {
  const { language } = useLanguage();
  const total = Object.values(distribution || {}).reduce((sum, val) => sum + val, 0);
  if (total === 0) return null;
  
  const levels = ['SALUDABLE', 'MODERADO', 'ALTO_RIESGO', 'CRITICO'];
  const center = size / 2;
  const strokeWidth = 20;
  const radius = (size / 2) - (strokeWidth / 2) - 5;
  
  let currentAngle = -90;
  
  const segments = levels.map(level => {
    const count = distribution[level] || 0;
    const percentage = (count / total) * 100;
    const angle = (percentage / 100) * 360;
    
    if (count === 0) return null;
    
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;
    
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    
    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);
    
    const largeArc = angle > 180 ? 1 : 0;
    const pathData = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
    
    return { level, count, percentage, pathData, color: IPS_LEVELS[level]?.color || '#9CA3AF' };
  }).filter(Boolean);
  
  // Texto centrado
  const label = language === 'es' ? 'empleados' : 'employees';
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Background circle */}
      <circle cx={center} cy={center} r={radius} fill="none" stroke="#E5E7EB" strokeWidth={strokeWidth} />
      {/* Colored segments */}
      {segments.map((segment) => (
        <path key={segment.level} d={segment.pathData} fill="none" stroke={segment.color} strokeWidth={strokeWidth} strokeLinecap="round" />
      ))}
      {/* Center text - número grande */}
      <text x={center} y={center + 8} textAnchor="middle" fontSize="36" fontWeight="bold" fill="#374151">
        {total}
      </text>
      {/* Label debajo */}
      <text x={center} y={center + 28} textAnchor="middle" fontSize="11" fill="#6B7280">
        {label}
      </text>
    </svg>
  );
};

/**
 * Company/Department IPS Summary Component - Redesigned v2
 * Layout: 3 columnas - Número IPS | Distribución | Donut Chart
 * Más compacto y mejor aprovechamiento del espacio
 */
export const IPSSummary = ({ 
  ips, 
  ipsLevel, 
  employeeCount,
  distribution,
  distributionPercentage,
  atRiskCount,
  healthyCount,
  minIps,
  maxIps,
  atRiskEmployees = [],
  onViewEmployeeReport
}) => {
  const { language } = useLanguage();
  const [alertExpanded, setAlertExpanded] = React.useState(false);
  
  const levelConfig = IPS_LEVELS[ipsLevel] || IPS_LEVELS.MODERADO;
  const levelLabel = LEVEL_LABELS[language]?.[ipsLevel] || ipsLevel;
  
  return (
    <div className="bg-white border rounded-xl p-4" style={{ borderLeftWidth: '4px', borderLeftColor: levelConfig.color }} data-testid="ips-summary-card">
      {/* 3 Column Layout */}
      <div className="grid grid-cols-3 gap-4 items-center">
        {/* Column 1: IPS Score */}
        <div className="text-center py-2">
          <p className="text-base font-bold text-gray-700 uppercase tracking-wide">
            SCORE MEVIA
          </p>
          <p className="text-sm text-gray-500 mb-1">
            {language === 'es' ? 'Permanencia Saludable' : 'Healthy Retention'}
          </p>
          <span className="text-5xl font-bold block" style={{ color: levelConfig.color }}>
            {ips?.toFixed(1) || 'N/A'}
          </span>
          <Badge className={`${levelConfig.bgColor} ${levelConfig.textColor} border-0 text-sm px-3 py-1 mt-2`}>
            {levelLabel}
          </Badge>
        </div>
        
        {/* Column 2: Distribution Counters */}
        <div className="border-x border-gray-100 px-4 py-1">
          {distribution && (
            <div className="space-y-1.5">
              {['SALUDABLE', 'MODERADO', 'ALTO_RIESGO', 'CRITICO'].map(level => {
                const count = distribution[level] || 0;
                const pct = distributionPercentage?.[level] || 0;
                const levelName = LEVEL_LABELS[language]?.[level] || level;
                
                return (
                  <div key={level} className="flex items-center gap-2">
                    <span 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: IPS_LEVELS[level]?.color }}
                    />
                    <span className="text-sm text-gray-600 flex-1 truncate">{levelName}</span>
                    <span className="text-lg font-bold text-gray-800 min-w-[2rem] text-right">{count}</span>
                    <span className="text-xs text-gray-400 min-w-[3rem] text-right">({pct.toFixed(0)}%)</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Column 3: Donut Chart - más grande */}
        {distribution && (
          <div className="flex items-center justify-center">
            <LargeDonutChart distribution={distribution} size={160} />
          </div>
        )}
      </div>
      
      {/* At Risk Alert - Expandable */}
      {atRiskCount > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setAlertExpanded(!alertExpanded)}
            className="w-full p-2.5 bg-red-50 rounded-lg flex items-center justify-between hover:bg-red-100 transition-colors"
            data-testid="at-risk-alert-toggle"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-sm font-medium text-red-700">
                {language === 'es'
                  ? `${atRiskCount} empleado${atRiskCount > 1 ? 's' : ''} en riesgo`
                  : `${atRiskCount} employee${atRiskCount > 1 ? 's' : ''} at risk`}
              </span>
            </div>
            {alertExpanded ? (
              <ChevronUp className="h-4 w-4 text-red-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-red-500" />
            )}
          </button>
          
          {/* Expanded Employee List */}
          {alertExpanded && atRiskEmployees && atRiskEmployees.length > 0 && (
            <div className="mt-2 border border-red-200 rounded-lg overflow-hidden" data-testid="at-risk-employees-list">
              {atRiskEmployees.map((emp, index) => (
                <div
                  key={emp.usuario_id || emp.user_id || index}
                  className="flex items-center justify-between p-2.5 hover:bg-red-50 border-b border-red-100 last:border-b-0"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                      style={{ backgroundColor: IPS_LEVELS[emp.ips_level]?.color || '#EF4444' }}
                    >
                      {emp.ips?.toFixed(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-800">{emp.nombre}</p>
                      <p className="text-xs text-gray-500">{emp.departamento || emp.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      className="text-xs border-0"
                      style={{ 
                        backgroundColor: `${IPS_LEVELS[emp.ips_level]?.color}20`,
                        color: IPS_LEVELS[emp.ips_level]?.color
                      }}
                    >
                      {LEVEL_LABELS[language]?.[emp.ips_level] || emp.ips_level}
                    </Badge>
                    {onViewEmployeeReport && (
                      <button
                        onClick={() => onViewEmployeeReport(emp)}
                        className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                        title={language === 'es' ? 'Ver reporte' : 'View report'}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IPSGauge;
